import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        /* SanCanDo 2026-10-28 (#167): first tap to an uncached colour showed a blank (white) frame because the
           whole gallery was replaced before its hero photo had loaded. Keep the old gallery on screen until the
           new hero image is loaded + decoded (4 s cap, errors swap anyway); a newer select cancels this one. */
        const token = {};
        this._sancandoSelectToken = token;
        return this.#preloadHero(newMediaGallery).then(() => {
          if (this._sancandoSelectToken !== token || !this.isConnected) return;
          this.replaceWith(newMediaGallery);
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * SanCanDo: resolves once the new gallery's first (hero) image is decoded, or after 4 s / on error.
   * @param {Element} gallery
   * @returns {Promise<void>}
   */
  #preloadHero(gallery) {
    const img = /** @type {HTMLImageElement | null} */ (
      gallery.querySelector('slideshow-slide:not([hidden]) img, img.product-media__image, img')
    );
    if (!img || typeof Image === 'undefined') return Promise.resolve();
    const src = img.getAttribute('src');
    if (!src) return Promise.resolve();
    return new Promise((resolve) => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        clearTimeout(cap);
        resolve();
      };
      const cap = setTimeout(done, 4000);
      try {
        const pre = new Image();
        const sizes = img.getAttribute('sizes');
        const srcset = img.getAttribute('srcset');
        if (sizes) pre.sizes = sizes.replace(/^auto,\s*/, '');
        if (srcset) pre.srcset = srcset;
        pre.onload = () => {
          if (typeof pre.decode === 'function') pre.decode().then(done, done);
          else done();
        };
        pre.onerror = done;
        pre.src = src;
        if (pre.complete && pre.naturalWidth > 0) done();
      } catch (e) {
        done();
      }
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
